import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBarCart } from "@/lib/api";
import { ArrowRight, Sparkles, BookOpen, Check } from "lucide-react";
import { getRetailerUrl } from "@/data/retailers";

export default function BarCart() {
  const [bottles, setBottles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBarCart().then(setBottles).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-ink" data-testid="bar-cart-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1600&q=85"
            alt="Bar"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/80 to-ink" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24">
          <p className="eyebrow">The Starter Bar Cart</p>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-cream leading-[1.02] max-w-3xl">
            Five bottles.<br /><span className="italic font-serif-editor text-pink-brand font-medium">Endless nights in.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-cream/75 font-body text-base sm:text-lg leading-relaxed">
            Ranked by how many cocktails in our database each spirit unlocks — the shortest possible shopping list for the most possible drinks. Beginners, start here.
          </p>
        </div>
      </section>

      {/* List */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {loading ? (
          <p className="text-center text-cream/60">Building your bar cart…</p>
        ) : (
          <div className="space-y-5 sm:space-y-6">
            {bottles.map((b, i) => (
              <article
                key={b.spirit}
                className="card-dark p-6 sm:p-8 grid md:grid-cols-12 gap-6 items-center"
                data-testid={`bar-cart-item-${b.spirit.toLowerCase()}`}
              >
                <div className="md:col-span-1">
                  <p className="font-serif-display text-5xl sm:text-6xl font-bold text-pink-brand leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                </div>
                <div className="md:col-span-7">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-cream">{b.spirit}</h2>
                    <span className="chip chip-pink">Unlocks {b.unlocks} cocktails · {b.percentage}%</span>
                  </div>
                  <p className="mt-3 text-cream/75 font-body">{b.why}</p>
                  <p className="mt-4 eyebrow">You can make</p>
                  <ul className="mt-2 space-y-1 text-cream/85 text-sm">
                    {b.example_cocktails.map(n => (
                      <li key={n} className="flex items-center gap-2"><Check size={14} className="text-pink-brand flex-shrink-0" /> {n}</li>
                    ))}
                  </ul>
                </div>
                <div className="md:col-span-4 flex flex-col gap-3">
                  <Link
                    to={`/cocktails`}
                    className="btn-pill btn-pink w-full"
                    data-testid={`explore-drinks-${b.spirit.toLowerCase()}`}
                  >
                    <BookOpen size={16} /> Explore {b.spirit} drinks
                  </Link>
                  {getRetailerUrl({ spirit: b.spirit }) ? (
                    <a
                      href={getRetailerUrl({ spirit: b.spirit })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-pill btn-outline-ink w-full"
                      data-testid={`find-retailers-${b.spirit.toLowerCase()}`}
                    >
                      Find retailers <ArrowRight size={16} />
                    </a>
                  ) : (
                    <p className="text-[11px] text-cream/50 text-center leading-relaxed font-body">
                      Retailer partners coming soon. Availability varies by state.
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Bonus advice */}
        <div className="mt-14 card-dark p-6 sm:p-8">
          <div className="flex items-center gap-2 text-pink-brand">
            <Sparkles size={16} />
            <p className="eyebrow">Sip Alchemy Tip</p>
          </div>
          <p className="mt-3 font-serif-display text-2xl sm:text-3xl font-bold text-cream">
            Add one bitter, one sweet, one citrus.
          </p>
          <p className="mt-3 text-cream/75 font-body">
            After the five spirits, stock <span className="text-cream font-semibold">Angostura bitters</span>, a <span className="text-cream font-semibold">sweet vermouth</span> and always keep <span className="text-cream font-semibold">fresh limes &amp; lemons</span>. That's a full home bar for under ₹6,000.
          </p>
        </div>
      </section>
    </div>
  );
}
